/**
 * A React component that renders a product carousel using Embla Carousel.
 * The carousel allows navigation through products with previous and next buttons.
 * 
 * @function ProductCarousel
 * @param {Object} sanity - The sanity data object containing the product list or tag.
 * @param {Object[]} related - A related array (unused in the code, may be part of future implementation).
 * @returns {JSX.Element} The rendered product carousel.
 * 
 * @example
 * <ProductCarousel sanity={sanityData} related={relatedData} />
 */
import React, {useCallback, useEffect, useState} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {Link, Image} from '@shopify/hydrogen';
import noImage from '../../assets/image/default-book-image-not-found.png';
import '../../assets/css/carousel.css';
import {getPath} from '../../lib/methods';
import deleteWish from '../../assets/image/delete-wish.svg';
import SpinnerIcon from '../SpinnerIcon.client';
import { roundDownToNearestTen } from '../../lib/Utils';

export default function ProductCarousel({sanity, related, customer, from}) {
  /**
   * @const viewportRef, embla - Embla Carousel instance and viewport reference.
   */
  const [viewportRef, embla] = useEmblaCarousel({
    containScroll: 'scrollSnap',
    slidesToScroll: 1,
    inViewThreshold: 1,
    skipSnaps: false,
    selectedClass: '',
    dragFree: true,
    align: 'start',
    loop: false,
  });

  const [product, setProduct] = useState(null);

  /**
   * @const product - Processed product list from sanity data.
   */

  useEffect(()=>{
    const product = sanity
    ? sanity?.listProducts
      ? sanity?.listProducts
      : sanity?.isTag
      ? generateProductItem(sanity.data)
      : []
    : [];

    try{
      const normalizeProduct = product.filter((item) =>  item.productItem.store.status == 'active' )
      setProduct(normalizeProduct);
    }catch(e){
      const normalizeProduct = product.filter((item) =>  item.store.status == 'active' )
      setProduct(normalizeProduct);
    }
  }, [])

  /**
   * @function generateProductItem
   * @param {Array} data - An array containing product items.
   * @returns {Array} The processed array of product items.
   */
  function generateProductItem(data) {
    const product = data.map((item) => {
      return {productItem: item};
    });
    return product;
  }

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [refreshCarrousel, setRefreshCarrousel] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  /**
   * @const scrollPrev - Function to scroll to the previous item in the carousel.
   * @function
   */
  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);

  /**
   * @const scrollNext - Function to scroll to the next item in the carousel.
   * @function
   */
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

 /**
   * @const scrollTo - Function to scroll to a specific index in the carousel.
   * @function
   * @param {number} index - The index to scroll to.
   */
  const scrollTo = useCallback(
    (index) => embla && embla.scrollTo(index),
    [embla],
  );

  /**
   * @const onSelect - Function to update the selected index and button states when an item is selected in the carousel.
   * @function
   */
  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  const toLowerCase = (string) => {
    let rstring = string;
    if (rstring) {
      rstring = rstring.toLowerCase();
      rstring = rstring.replace(/ /g, '_');
    }
    if (rstring.includes('-')) {
      rstring = rstring.replace(/-/g, '');
    }
    return rstring;
  };

  const removeWishList = async(productId)=> {
    setRefreshCarrousel(true);

    let newW;
    const wishlist = product;
    const found = wishlist.find((item) => item._id === productId);
    let newWList = [];
    if (found) {
      newWList = wishlist.filter((item) => item._id !== productId);
      newW = newWList.map((item) => {return item.store.gid;})
    }

    if (!newW) {
      return;
    }

    fetch(
      `https://api.ellibrero.com/api/wishlist/mutate`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({productId: newW, customerId: customer.id}),
      },
    )
    .then((res) => {
      setProduct(newWList);
      setRefreshCarrousel(false);
      if(newWList.length === 0){
        location.reload();
      }
    })
    .catch((error) => {
    });

  }

  const discount = (product)=>{
    if(product?.priceRange?.maxVariantPrice?.amount){
      let min = parseInt(product.priceRange.maxVariantPrice.amount);
      let max = 1;

      product.variants.forEach((price)=>{ max = (price.store.compareAtPrice>max)?price.store.compareAtPrice:max; })

      let discountPercent = Math.round(((max-min) / max) * 100);

      return roundDownToNearestTen(discountPercent);
    }
  }
/**
 * @returns {JSX.Element} A section element representing a product carousel.
 * - The class name of the section includes 'section_librero', 'products_carousel', and the sanity type (if it exists), converted to lowercase.
 * - The background style can be solid, an image, a gradient, or a default color.
 * - Inside the section, there is a div container for the carousel, including the title and navigation buttons.
 * - The carousel itself is represented as a series of product items with details such as images, title, price, and possibly a badge indicating it's an eBook.
 * - Conditional rendering ensures that only active product items are displayed.
 * - Links are provided for each product, leading to its detailed view.
 * - Product titles that exceed 100 characters are truncated with an ellipsis.
 * - Prices are formatted to a minimum of two decimal places, and sale prices (if available) are displayed with a strikethrough.
 */
  return (
    <div className="section_container products_carousel">
      {product && ( 
        <section
          className={`section_librero products_carousel  ${
            sanity.type ? toLowerCase(sanity.type) : ''
          }`}
          style={{
            background:
              sanity.backgroundType == 'Solid'
                ? sanity.backgroundColor
                : sanity.backgroundType == 'Image'
                ? `url(${sanity.backgroundImage.asset.url})`
                : sanity.backgroundType == 'Gradient'
                ? `linear-gradient(90deg, ${sanity.backgroundGradient.gradientFrom} 0%, ${sanity.backgroundGradient.gradientTo} 100%)`
                : '#00000',
            backgroundSize: 'cover',
            padding: sanity.backgroundType ? '30px 0 50px 0' : '0 0 0 0',
            margin: sanity.backgroundType ? '0 0' : '30px 0',
          }}
        >
          <div className="librero_carousel container md:mx-auto">
            <div className="embla relative">
              <h2 className="section-title mb-5 flex flex-row gap-2 items-center">
                {sanity.title}
                {sanity.redirectionType && (
                  <div className="ver_todos ml-2">
                    <Link to={getPath(sanity)} reloadDocument="true">Ver todos</Link>
                  </div>
                )}
              </h2>
              {!refreshCarrousel && product && product.length > 0 && (
                  <div className="product_carousel_wrapper px-12 mx-auto relative xs:px-0">
                    <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
                    <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
                    <div className="carousel_inner overflow-hidden">
                      <div ref={viewportRef}>
                        <div className="embla__container">
                          {product
                            ? product.map((item) => {
                                if (
                                  ((item.productItem || item.store) &&
                                    item.productItem?.store?.status == 'active') ||
                                  item.store?.status == 'active'
                                ) {
                                  return (
                                    <div
                                      className="md:embla__slide--thumb product_slide xs:w-full relative"
                                      key={item._key ? item._key : 'prod-' + item._id}
                                      style={{height: 'auto'}}
                                    >
                                      {discount(item.productItem?.store) > 50 && ( 
                                        <div className='tag-discount tag-carroucel'>
                                          <span>- {discount(item.productItem?.store)}%</span>
                                        </div>
                                      )}
                                      <div 
                                          className={`
                                            delete-button
                                            ${(from !== 'panelUser') ? 'hidden' : ''}
                                          `} 
                                      >
                                        <Image
                                          src={deleteWish}
                                          height={25}
                                          width={35}
                                          onClick={()=>{removeWishList(item._id)}}
                                        />
                                      </div>
                                      <Link
                                        className="card_link carousel-card"
                                        to={
                                          item.productItem
                                            ? 'producto/' +
                                              item.productItem.store.slug.current
                                            : 'producto/' + item.store.slug.current
                                        }
                                      >
                                        {item.tag ? (
                                          <div className="card-badge font-13 leading-none text-white py-2 text-sm font-semibold px-4 rounded-full bg-purple-120 absolute -top-4 left-0">
                                            {item.tag}
                                          </div>
                                        ) : (
                                          ''
                                        )}
                                        <div className="product__card card rounded-xl border-white bg-white">
                                          <div className="justify-center items-center flex">
                                            <div className="product__card_image_wrapper relative w-full flex items-center">
                                              {item.productItem?.store.productType ==
                                                'ebooks' && (
                                                <>
                                                  <div className="badge-ebook bg-purple-120">
                                                    eBook
                                                  </div>
                                                </>
                                              )}
                                              <Image
                                                src={
                                                  item.productItem
                                                    ? item.productItem.store?.previewImageUrl + '&width=159&height=185&quality=99'
                                                      ? item.productItem.store?.previewImageUrl + '&width=159&height=185&quality=99'
                                                      : noImage
                                                    : item.store.previewImageUrl
                                                    ? item.store.previewImageUrl + '&width=159&height=185&quality=99'
                                                    : noImage
                                                }
                                                height={185}
                                                width={159}
                                                className="product__card_image"
                                                alt={
                                                  item.productItem
                                                    ? item.productItem.store.title
                                                    : item.store.title
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div
                                            style={{height: '90px'}}
                                            className=" bg-[#EDF0F3] p-3 justify-between flex-col flex rounded-br-xl rounded-bl-xl "
                                          >
                                            <div>
                                              <h3 className="leading-tight line-clamp">
                                                {item.productItem
                                                  ? item.productItem.store.title
                                                      .length > 100
                                                    ? item.productItem.store.title.slice(
                                                        0,
                                                        99,
                                                      ) + '...'
                                                    : item.productItem.store.title
                                                  : item.store.title.length > 100
                                                  ? item.store.title.slice(0, 99) +
                                                    '...'
                                                  : item.store.title}
                                              </h3>
                                            </div>
                                            <div className="card_price_wrapper flex content-end items-end gap-1">
                                              <span className="card_price text-red-40 font-bold">
                                                MXN&nbsp;
                                                {item.productItem
                                                  ? item.productItem?.store.variants !=
                                                      null &&
                                                    item.productItem?.store.variants[0]
                                                      .store.compareAtPrice == 0
                                                    ? item.productItem.store.variants[0].store.price.toLocaleString(
                                                        'en',
                                                        {minimumFractionDigits: 2},
                                                      )
                                                    : item.productItem.store.variants[0].store.price.toLocaleString(
                                                        'en',
                                                        {minimumFractionDigits: 2},
                                                      )
                                                  : item.store.variants != null &&
                                                    item.store.variants.length != 0
                                                  ? item.store.variants[0].store.price.toLocaleString(
                                                      'en',
                                                      {
                                                        minimumFractionDigits: 2,
                                                      },
                                                    )
                                                  : item.store.priceRange != null
                                                  ? item.store.priceRange
                                                      .maxVariantPrice
                                                  : ''}
                                              </span>
                                              <span className="sale-price mb-0 text-xs leading-none text-purple-40 line-through">
                                                {item.productItem
                                                  ? item.productItem.store.variants !=
                                                      null &&
                                                    item.productItem.store.variants[0]
                                                      .store.compareAtPrice != 0
                                                    ? '$' +
                                                      item.productItem.store.variants[0].store.compareAtPrice.toLocaleString(
                                                        'en',
                                                        {minimumFractionDigits: 2},
                                                      )
                                                    : ''
                                                  : ''}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </Link>
                                    </div>
                                  );
                                }
                              })
                            : ''}
                        </div>
                      </div>
                    </div>
                  </div>
              )}
              {refreshCarrousel ? <SpinnerIcon /> : <div></div>}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * @function PrevButton
 * @param {Object} props - The component props.
 * @param {boolean} props.enabled - Whether the button is enabled.
 * @param {function} props.onClick - The click handler for the button.
 * @returns {JSX.Element} The PrevButton component.
 */
const PrevButton = ({enabled, onClick}) => (
  <button
    className={` ${
      !enabled ? 'disabled' : 'fill-purple-120'
    } btn-prev xs:hidden left-0 absolute bottom-46 flex items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer`}
    onClick={onClick}
    disabled={!enabled}
  >
    <svg
      className={` ${!enabled ? 'fill-white' : 'fill-purple-120'}
          embla__button__svg h-5 w-5  fill-purple-120
        `}
      viewBox="137.718 -1.001 366.563 644"
    >
      <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
    </svg>
  </button>
);

/**
 * @function NextButton
 * @param {Object} props - The component props.
 * @param {boolean} props.enabled - Whether the button is enabled.
 * @param {function} props.onClick - The click handler for the button.
 * @returns {JSX.Element} The NextButton component.
 */
const NextButton = ({enabled, onClick}) => {
  return (
    <button
      className={` ${
        !enabled ? 'disabled' : 'fill-purple-120'
      } btn-next xs:hidden absolute right-0 bottom-46 flex items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer`}
      onClick={onClick}
      disabled={!enabled}
    >
      <svg
        className={`embla__button__svg  h-5 w-5 fill-purple-120  ${
          !enabled ? 'fill-white' : 'fill-purple-120'
        }`}
        viewBox="0 0 238.003 238.003"
      >
        <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
      </svg>
    </button>
  );
};
